import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowUpRight, CaretDown, Check } from '@phosphor-icons/react';
import { useSEO } from '../lib/seo';
import { Reveal, Chapter, PageHero } from '../lib/motion';
import { GUIDES, findGuide } from '../data/guides';

const chipCls =
  'inline-flex items-center gap-2 border border-line px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-steel transition-colors duration-200 hover:border-volt/60 hover:text-volt';

const FaqItem = ({ q, a, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-line bg-surface" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-display text-lg font-bold text-parchment">{q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 text-volt transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="border-t border-line px-6 pb-6 pt-4 text-base leading-relaxed text-steel" data-testid={`${testId}-answer`}>{a}</p>}
    </div>
  );
};

const GuidePage = () => {
  const { slug } = useParams();
  const guide = findGuide(slug);

  const path = guide ? `/guides/${guide.slug}` : '/guides';
  const pageJsonLd = guide
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: guide.h1.join(' '),
          description: guide.description,
          url: `https://voxmeridian.com${path}`,
          isPartOf: { '@type': 'WebSite', name: 'Vox Meridian', url: 'https://voxmeridian.com' },
          publisher: { '@type': 'Organization', name: 'Vox Meridian', url: 'https://voxmeridian.com' },
          about: guide.keyword,
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: guide.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Guides', item: 'https://voxmeridian.com/guides' },
            { '@type': 'ListItem', position: 2, name: guide.h1.join(' '), item: `https://voxmeridian.com${path}` },
          ],
        },
      ]
    : null;

  useSEO({
    title: guide ? guide.title : 'Guide | Vox Meridian',
    description: guide ? guide.description : '',
    path,
    jsonLd: pageJsonLd,
  });

  if (!guide) return <Navigate to="/guides" replace />;

  return (
    <div className="bg-ink" data-testid="guide-page">
      <PageHero eyebrow="Guide" lines={guide.h1} sub={guide.lead}>
        <p className="mono-label mt-8 text-steel" data-testid="guide-read-time">{guide.readTime}</p>
      </PageHero>

      {/* Key takeaways — designed for AI extraction */}
      <section className="border-b border-line bg-ink" data-testid="guide-takeaways-section">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
          <Reveal>
            <div className="border-l-4 border-volt bg-surface p-8" data-testid="guide-takeaways">
              <p className="mono-label text-volt">Key takeaways</p>
              <ul className="mt-5 space-y-3">
                {guide.takeaways.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-base text-parchment">
                    <Check size={16} weight="bold" className="mt-1 shrink-0 text-volt" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Body with sticky TOC */}
      <section className="border-b border-line bg-ink">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:px-10">
          <aside className="lg:col-span-3">
            <Reveal className="lg:sticky lg:top-24">
              <p className="mono-label text-volt">On this page</p>
              <nav className="mt-5 space-y-2" data-testid="guide-toc">
                {guide.sections.map((s, i) => (
                  <a key={s.h} href={`#section-${i}`} data-testid={`guide-toc-${i}`} className="block font-mono text-sm tracking-tight text-steel transition-colors duration-200 hover:text-volt">
                    {String(i + 1).padStart(2, '0')}. {s.h}
                  </a>
                ))}
                <a href="#faq" data-testid="guide-toc-faq" className="block font-mono text-sm tracking-tight text-steel transition-colors duration-200 hover:text-volt">
                  {String(guide.sections.length + 1).padStart(2, '0')}. FAQ
                </a>
              </nav>
            </Reveal>
          </aside>

          <div className="max-w-3xl lg:col-span-9">
            {guide.sections.map((s, i) => (
              <div key={s.h} id={`section-${i}`} className="mt-14 first:mt-0 scroll-mt-24" data-testid={`guide-section-${i}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-sm font-semibold text-volt">/{String(i + 1).padStart(2, '0')}</span>
                  <Reveal>
                    <h2 className="font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">{s.h}</h2>
                  </Reveal>
                </div>
                <div className="mt-6 space-y-5">
                  {s.paragraphs.map((p, j) => (
                    <Reveal key={j} delay={j * 0.05}>
                      <p className="text-base leading-relaxed text-steel">{p}</p>
                    </Reveal>
                  ))}
                </div>
                {s.list && (
                  <Reveal delay={0.1}>
                    <ul className="mt-6 space-y-3 border border-line bg-surface p-6">
                      {s.list.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-base text-parchment">
                          <Check size={15} weight="bold" className="mt-1 shrink-0 text-volt" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                )}
              </div>
            ))}

            {/* FAQ feeds FAQPage JSON-LD */}
            <div id="faq" className="mt-16 scroll-mt-24" data-testid="guide-faq">
              <Chapter no={String(guide.sections.length + 1).padStart(2, '0')} label="Questions" title="Frequently asked." />
              <div className="mt-8 space-y-3">
                {guide.faq.map((f, i) => (
                  <FaqItem key={f.q} q={f.q} a={f.a} testId={`guide-faq-${i}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dual CTA — honest, routes to RFQ or Service Manager */}
      <section className="border-b border-line bg-surface" data-testid="guide-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-16 sm:flex-row sm:items-center sm:justify-between lg:px-10">
          <div>
            <p className="mono-label text-volt">Put it into practice</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">
              Ready when you are.
            </h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to={guide.cta.primary.to}
              data-testid="guide-cta-primary"
              className="group flex shrink-0 items-center justify-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5"
            >
              {guide.cta.primary.label}
              <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to={guide.cta.secondary.to}
              data-testid="guide-cta-secondary"
              className="flex shrink-0 items-center justify-center gap-2 border border-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-volt transition-colors duration-200 hover:bg-volt hover:text-ink"
            >
              {guide.cta.secondary.label}
            </Link>
          </div>
        </div>
      </section>

      {/* Related guides */}
      {guide.related?.length > 0 && (
        <section className="bg-ink" data-testid="guide-related">
          <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
            <p className="mono-label text-volt">Keep reading</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {guide.related.map((r) => {
                const rel = findGuide(r);
                return rel ? (
                  <Link key={r} to={`/guides/${r}`} data-testid={`guide-related-${r}`} className={chipCls}>
                    {rel.h1.join(' ')}
                  </Link>
                ) : null;
              })}
              <Link to="/guides" data-testid="guide-related-all" className={chipCls}>
                All guides
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default GuidePage;
